import { Box, Button, Step, StepLabel, Stepper, CircularProgress, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import IntroStep from "./components/IntroStep";
import DetailsStep from "./components/DetailsStep";
import BudgetStep from "./components/BudgetStep";
import ReviewStep from "./components/ReviewStep";
import { useAppDispatch, useAppSelector } from "../../Redux/store";
import { useGenerateJobSuggestionMutation } from "../../Redux/api/kaziAiApi";
import {
  setTitle,
  setLevel,
  setTags,
  setDescription,
  setResponsibilities,
  setSkills,
  setPayType,
  setPayAmount,
  setPayAmountMax,
  setJobType,
  setPayRate,
  setDuration,
  setDurationType,
  setPrimaryLanguage,
  setQuestions,
  setWorkSite,
  setSpecialization,
  setDetails,
  setRegionalRates,
  setActiveStep,
  setIsLoading,
  resetJobPost,
  setEditingJobLocator,
  setDeliverables,
} from "../../Redux/slices/JobPostCreationSlice";
import { useUser } from "../../Hooks/useUser";
import { AddJobRequest, JobStatus } from "../../Types/Job";
import { useToast } from "../../Hooks/useToast";
import { useAddJobMutation, useUpdateJobMutation } from "../../Redux/api/jobApi";
import { useKaziNavigate } from "../../Hooks/useKaziNavigate";
import { KaziButton } from "../../Shared Components/Kazi/KaziButton";
import { useLazyCreateSetupIntentQuery } from "../../Redux/api/stripeApi";
import { setClientSecret, setCustomerSessionClientSecret } from "../../Redux/slices/GlobalSlice";
import { setShowAddPaymentMethodModal } from "../../Redux/slices/GlobalModalSlice";

const steps = ["Intro", "Details", "Budget", "Review"];

export const JobPostCreation = () => {
  const dispatch = useAppDispatch();
  const { showToast } = useToast();
  const [generateJobSuggestion, {isLoading: suggestionsLoading}] = useGenerateJobSuggestionMutation();
  const { locator, invalidateUser, hasDefaultPaymentMethod } = useUser();
  const [addJob, {data: jobResponse, isLoading: jobResponseLoading}] = useAddJobMutation();
  const [updateJob] = useUpdateJobMutation();
  const { navigateToJob } = useKaziNavigate();
  const [createSetupIntent] = useLazyCreateSetupIntentQuery();
  const navigate = useNavigate();

  const {
    activeStep,
    details,
    title,
    level,
    tags,
    description,
    responsibilities,
    skills,
    payType,
    payAmount,
    payAmountMax,
    jobType,
    payRate,
    duration,
    durationType,
    primaryLanguage,
    questions,
    workSite,
    specialization,
    editingJobLocator,
    creationMethod,
    deliverables,
  } = useAppSelector((state) => state.jobPostCreation);

  const handleNext = async () => {
    if (activeStep === 3){
      handleFinalize();
      return;
    }
    if (activeStep === 1) {
      try {
        const response = await generateJobSuggestion({
          description: details
        }).unwrap();
        
        // Update form state with AI suggestions
        dispatch(setTitle(response.title));
        dispatch(setLevel(response.level));
        dispatch(setTags(response.tags));
        dispatch(setDescription(response.description));
        dispatch(setResponsibilities(response.responsibilities));
        dispatch(setSkills(response.skills));
        dispatch(setPayType('Fixed' /*response.pay_type*/));
        dispatch(setPayAmount(response.pay_amount.toString()));
        dispatch(setPayAmountMax(response.pay_amount_max?.toString() || ""));
        dispatch(setJobType(response.type));
        dispatch(setPayRate(response.pay_rate));
        dispatch(setDuration(response.duration.toString()));
        dispatch(setDurationType(response.duration_type));
        dispatch(setPrimaryLanguage(response.primary_language || ""));
        dispatch(setQuestions(response.questions));
        dispatch(setWorkSite(response.work_site));
        dispatch(setSpecialization(response.specialization));
        dispatch(setDeliverables(response.deliverables || []));
        
        // Convert market rates to regional rates format
        const rates = Object.entries(response.market_rates).map(([region, rates]) => ({
          region,
          rate: response.pay_type === 'Fixed' 
            ? `$${rates.fixed.toLocaleString()}`
            : `$${rates.range.min.toLocaleString()}-${rates.range.max.toLocaleString()}`
        }));
        dispatch(setRegionalRates(rates));
      } catch (error) {
        console.error('Failed to generate job suggestion:', error);
      } 
    }
    dispatch(setActiveStep(activeStep + 1));
  };

  const handleBack = () => dispatch(setActiveStep(activeStep - 1));

  const handleAddPaymentMethod = () => {
    if (!hasDefaultPaymentMethod) {
      createSetupIntent(locator!)
        .unwrap()
        .then(res => {
          dispatch(setClientSecret(res.clientSecret));
          dispatch(setCustomerSessionClientSecret(res.clientSecret));
          dispatch(setShowAddPaymentMethodModal(true));
        })
        .catch(err => {
          console.error("Error occurred while creating setup intent: ", err);
          showToast({
            text: "Unable to connect payment",
            variant: "error"
          });
        });
    }
  };

  const handleFinalize = () => {
    if (!hasDefaultPaymentMethod) {
      handleAddPaymentMethod();
      return;
    }

    // Finalize logic here
    const addJobRequest: AddJobRequest = {
      client_locator: locator!,
      title,
      level,
      tags,
      description,
      responsibilities,
      skills: skills.map(skill => ({skill, qualification: 'must_have'})),
      pay_type: payType as 'Fixed' | 'Range',
      pay_amount: Number(payAmount),
      pay_amount_max: payType === 'Range' ? Number(payAmountMax) : undefined,
      type: jobType,
      pay_rate: payRate,
      duration: Number(duration),   
      pay_currency: 'USD',
      duration_type: jobType === "Internship" ? durationType : undefined,
      primary_language: primaryLanguage || '',
      questions: questions.map( (q, idx) => ({id: idx, question: q})),
      work_site: workSite,
      specialization,
      status: JobStatus.Open,
      creation_method: 'ai',
      deliverables: deliverables || []
    }
    // console.log('Job post to be created:', addJobRequest);
    addJob(addJobRequest)
    .unwrap()
    .then(res => {
        invalidateUser();
        dispatch(resetJobPost());
        navigateToJob(res.createJob.locator);
    })
    .catch(err => {
        console.error('Error creating job post:', err)
        showToast({
            text: "Unable to create job post",
            variant: 'error'
        })
    })
  };

  const handleSaveAsDraft = () => {
    if (editingJobLocator) {
      // Update existing draft
      const updateJobRequest = {
        locator: editingJobLocator,
        updated_data: {
          title: title || 'Untitled Job',
          level,
          tags,
          description: description || '',
          responsibilities,
          skills: skills.map(skill => ({skill, qualification: 'must_have'})),
          pay_type: payType as 'Fixed' | 'Range',
          pay_amount: Number(payAmount) || 0,
          pay_amount_max: payType === 'Range' ? Number(payAmountMax) : undefined,
          type: jobType || 'Short-term',
          pay_rate: payRate || 'Hourly',
          duration: Number(duration) || 30,   
          pay_currency: 'USD',
          duration_type: jobType === "Internship" ? durationType : undefined,
          primary_language: primaryLanguage || '',
          questions: questions.map( (q, idx) => ({id: idx, question: q})),
          work_site: workSite || 'Remote',
          specialization: specialization || 'General',
          status: JobStatus.Draft,
          creation_method: 'ai',
          deliverables: deliverables || []
        }
      };
      
      updateJob(updateJobRequest)
      .unwrap()
      .then(res => {
          invalidateUser();
          dispatch(resetJobPost());
          navigate('/job-postings');
          showToast({
              text: "Draft updated successfully",
              variant: 'success'
          });
      })
      .catch(err => {
          console.error('Error updating draft:', err)
          showToast({
              text: "Unable to update draft",
              variant: 'error'
          })
      })
    } else {
      // Create new draft
      const saveDraftRequest: AddJobRequest = {
        client_locator: locator!,
        title: title || 'Untitled Job',
        level,
        tags,
        description: description || '',
        responsibilities,
        skills: skills.map(skill => ({skill, qualification: 'must_have'})),
        pay_type: payType as 'Fixed' | 'Range',
        pay_amount: Number(payAmount) || 0,
        pay_amount_max: payType === 'Range' ? Number(payAmountMax) : undefined,
        type: jobType || 'Short-term',
        pay_rate: payRate || 'Hourly',
        duration: Number(duration) || 30,   
        pay_currency: 'USD',
        duration_type: jobType === "Internship" ? durationType : undefined,
        primary_language: primaryLanguage || '',
        questions: questions.map( (q, idx) => ({id: idx, question: q})),
        work_site: workSite || 'Remote',
        specialization: specialization || 'General',
        status: JobStatus.Draft,
        creation_method: 'ai',
        deliverables: deliverables || []
      }
      
      addJob(saveDraftRequest)
      .unwrap()
      .then(res => {
          invalidateUser();
          dispatch(resetJobPost());
          navigate('/job-postings');
          showToast({
              text: "Draft saved successfully",
              variant: 'success'
          });
      })
      .catch(err => {
          console.error('Error saving draft:', err)
          showToast({
              text: "Unable to save draft",
              variant: 'error'
          })
      })
    }
  };

  // Validation function to check if all required sections are populated for publishing
  const getRequiredSectionsValidation = () => {
    return {
      title: title && title.trim() !== '',
      level: level && level.trim() !== '',
      tags: tags && tags.length > 0,
      description: description && description.trim() !== '',
      responsibilities: responsibilities && responsibilities.length > 0,
      skills: skills && skills.length > 0,
      payType: payType && payType.trim() !== '',
      payAmount: payAmount && payAmount.trim() !== '' && Number(payAmount) > 0,
      jobType: jobType && jobType.trim() !== '',
      payRate: payRate && payRate.trim() !== '',
      duration: duration && duration.trim() !== '' && Number(duration) > 0,
      workSite: workSite && workSite.trim() !== '',
      specialization: specialization && specialization.trim() !== ''
    };
  };

  const isJobReadyForPublishing = () => {
    const requiredSections = getRequiredSectionsValidation();
    return Object.values(requiredSections).every(Boolean);
  };

  // Helper function to get missing required sections
  const getMissingSections = () => {
    const requiredSections = getRequiredSectionsValidation();
    return Object.entries(requiredSections)
      .filter(([_, isValid]) => !isValid)
      .map(([section, _]) => {
        // Map internal field names to user-friendly names
        const fieldNameMap: { [key: string]: string } = {
          title: 'Title',
          level: 'Level',
          tags: 'Tags',
          description: 'Description',
          responsibilities: 'Responsibilities',
          skills: 'Qualifications',
          payType: 'Payment Type',
          payAmount: 'Budget',
          jobType: 'Job Type',
          payRate: 'Payment Frequency',
          duration: 'Duration',
          workSite: 'Work Site',
          specialization: 'Specialization'
        };
        return fieldNameMap[section] || section.charAt(0).toUpperCase() + section.slice(1).replace(/([A-Z])/g, ' $1');
      });
  };

  const getStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <IntroStep/>
        );
      case 1:
        return (
          <DetailsStep
            value={details}
            onChange={(value) => dispatch(setDetails(value))}
          />
        );
      case 2:
        return (
          <BudgetStep
          />
        );
      case 3:
        return (
          <ReviewStep
          />
        );
      default:
        return null;
    }
  };

  return (
    <Box width="100%" maxWidth={900} mx="auto" mt={6}>
      <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {suggestionsLoading ? (
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="60vh">
          <CircularProgress size={60} />
          <Typography variant="h6" mt={2}>
            Generating job post...
          </Typography>
        </Box>
      ) : (
        <Box my={4}>
          {getStepContent(activeStep)}
        </Box>
      )}

      {/* Show validation message when publish button is disabled */}
      {activeStep === steps.length - 1 && !isJobReadyForPublishing() && (
        <Box mt={2} p={2} bgcolor="warning.light" borderRadius={1}>
          <Typography variant="body2" color="warning.dark">
            <strong>Please complete the following required sections before publishing:</strong>
          </Typography>
          <Typography variant="body2" color="warning.dark" mt={1}>
            {getMissingSections().map(section => 
              section.charAt(0).toUpperCase() + section.slice(1).replace(/([A-Z])/g, ' $1')
            ).join(', ')}
          </Typography>
          <Typography variant="body2" color="warning.dark" mt={1}>
            Note: Deliverables and Questions are optional and can be skipped.
          </Typography>
        </Box>
      )}

      {activeStep !== 0 && (
        <Box display="flex" justifyContent="space-between" alignItems="center" mt={4} mb={6}>
          <Button
            variant="outlined"
            onClick={handleBack}
            disabled={suggestionsLoading}
            sx={{ 
              px: 2,
              py: 1,
              fontSize: '0.875rem',
              opacity: creationMethod === 'manual' ? 0 : 1,
              pointerEvents: creationMethod === 'manual' ? 'none' : 'auto'
            }}
          >
            Back
          </Button>
          <Box display="flex" gap={2}>
            {(activeStep === 2 || activeStep === 3) && (
              <Button
                variant="outlined"
                onClick={handleSaveAsDraft}
                disabled={suggestionsLoading || jobResponseLoading}
                sx={{ 
                  px: 2,
                  py: 1,
                  fontSize: '0.875rem'
                }}
              >
                Save as Draft
              </Button>
            )}
            <KaziButton
              text={activeStep === steps.length - 1 ? (hasDefaultPaymentMethod ? 'Publish Job Post' : 'Add Payment Method') : 'Next'}
              onClick={handleNext}
              disabled={(activeStep === 1 && !details.trim()) || suggestionsLoading || (activeStep === steps.length - 1 && !isJobReadyForPublishing())}
              loading={{
                isLoading: jobResponseLoading,
                loadingText: "Publishing..."
              }}
              variant="contained"
              size="small"
              sx={{
                px: 2,
                py: 1,
                fontSize: '0.875rem'
              }}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default JobPostCreation; 